import { Fragment, useCallback, useMemo } from 'react';
import { __OPERATION__EVENT__ADMIN__ } from 'shell/constants/environment';
import Table from 'shell/components/shared/table';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { useEffect } from 'react';
import { useFormik } from 'formik';
// import { transformDataset } from './utils';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { initialValues } from '..';
import { useNavigate } from 'react-router-dom';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import PmiTitle from 'shell/components/shared/title';
import {
  PublishedEventsResponse,
  transformPublishedEvents,
} from '../utils/transform-published-events';
import { FiltersFormPublishedEvents, PublishedEventsClient } from './types';
import { PublishedEvent } from 'shell/models/pubblished-event';
import { transformDataset } from './utils';
import FiltersPublishedEvents from 'system/components/published-events/list';
import { authsObj, headCells, validationSchemaPublishedEventsFilter } from './constants';
import { CommonPublishedEventsCrumbs } from '../constants';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { Typography } from '@mui/material';

const PublishedEventsListPage = (): React.ReactElement => {
  const filterTable = useFilterTable<FiltersFormPublishedEvents>();

  const navigate = useNavigate();

  const auths = useActionAuthorization(authsObj);

  const [publishedEventsList, getPublishedEvents] = useHttpFetch<
    HttpResponseList<PublishedEvent>,
    PublishedEventsResponse
  >(`${__OPERATION__EVENT__ADMIN__}/eventoPubblicato/list`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: {},
    },
    transformer: transformPublishedEvents,
  });

  const onSubmit = useCallback(
    (formValues: FiltersFormPublishedEvents) => {
      filterPublishedEventsForm.setFieldValue('page', 0);
      const queryString = transformDataset({ ...formValues, page: 0 });
      getPublishedEvents({
        params: {},
        paramsSerializer: () => queryString,
      });
    },
    [getPublishedEvents]
  );

  const filterPublishedEventsForm = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchemaPublishedEventsFilter,
    onSubmit,
  });

  useEffect(() => {
    filterPublishedEventsForm.setValues(filterTable.values || initialValues);
    const queryString = transformDataset(filterTable.values || initialValues);
    getPublishedEvents({
      params: {},
      paramsSerializer: () => queryString,
    });
  }, []);

  const onSort = useCallback(
    (orders) => {
      const newDataset = { ...filterPublishedEventsForm.values, orders };
      const queryString = transformDataset(newDataset);
      (
        getPublishedEvents({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<PublishedEvent>, PublishedEventsResponse>
      ).then(() => {
        filterPublishedEventsForm.setFieldValue('orders', orders);
      });
    },
    [getPublishedEvents, filterPublishedEventsForm.values, filterPublishedEventsForm.setValues]
  );

  const onVisibilityPress = useCallback(
    (cell): Promise<PublishedEventsClient> => {
      return new Promise((resolve) => {
        navigate(
          `/sistema/eventmanager/ricerca-eventi-pubblicati/dettaglio-evento-pubblicato/${cell.id}`
        );
        resolve(cell);
      });
    },
    [navigate]
  );

  const handleChangePage = useCallback(
    (newPage) => {
      const newDataset = { ...filterPublishedEventsForm.values, page: newPage || 0 };
      const queryString = transformDataset(newDataset);
      (
        getPublishedEvents({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<PublishedEvent>, PublishedEventsResponse>
      ).then(() => {
        filterPublishedEventsForm.setFieldValue('page', newPage);
      });
    },
    [getPublishedEvents, filterPublishedEventsForm.values, filterPublishedEventsForm.setFieldValue]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = {
        ...filterPublishedEventsForm.values,
        rowsPerPage: rowsPerPage || 0,
        page: 0,
      };
      const queryString = transformDataset(newDataset);
      (
        getPublishedEvents({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<PublishedEvent>, PublishedEventsResponse>
      ).then(() => {
        filterPublishedEventsForm.setValues(newDataset);
      });
    },
    [getPublishedEvents, filterPublishedEventsForm.setFieldValue, filterPublishedEventsForm.values]
  );

  const count: number = useMemo(() => {
    return (publishedEventsList.data as PublishedEventsResponse)?.pageInfo?.totalCount || 0;
  }, [publishedEventsList.data]);

  return (
    <Fragment>
      <Breadcrumbs items={CommonPublishedEventsCrumbs} />
      <PmiTitle title="Ricerca Eventi Pubblicati" />
      <div className="pmi-my-3">
        <FiltersPublishedEvents filterPublishedEventsForm={filterPublishedEventsForm} />
      </div>
      {publishedEventsList.data ? (
        (publishedEventsList.data as PublishedEventsResponse)?.pubblishedEvents?.length > 0 ? (
          <Table
            onSort={onSort}
            head={headCells}
            isServer
            orders={filterPublishedEventsForm.values.orders}
            activeActions={auths}
            page={filterPublishedEventsForm.values.page}
            rowsPerPage={filterPublishedEventsForm.values.rowsPerPage}
            body={(publishedEventsList.data as PublishedEventsResponse)?.pubblishedEvents}
            onVisibilityPress={onVisibilityPress}
            count={count || 0}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ariaLabel="Ricerca Eventi Pubblicati"
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
    </Fragment>
  );
};

PublishedEventsListPage.displayName = 'Published Events List page component';

export default PublishedEventsListPage;
